/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useRef } from 'react';
import {
  Users as UsersIcon,
  UserPlus,
  Search,
  Edit2,
  Trash2,
  ChevronDown,
  Check
} from 'lucide-react';
import { userService } from '../services/userService';
import { roleService } from '../services/roleService';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import toastService from '../utils/toastService';
import { usePermissions } from '../context/PermissionContext';
import { useNavigate } from 'react-router-dom';
import { toLocal } from '../utils/dateTime';

const SEARCH_DEBOUNCE_MS = 500;
const CLASS_BG_PRIMARY_MUTED = 'bg-primary/5';
const CLASS_TEXT_PRIMARY = 'text-primary';
const CLASS_TEXT_MUTED_HOVER = 'text-base-content/40 group-hover:text-primary';

const Users = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState({ search: '', status: 'All', role: 'All' });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [availableRoles, setAvailableRoles] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);

  // ─── Refs to always have latest values without being deps ───────────────────
  const paginationRef = useRef(pagination);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  // ─── Debounce search ────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Outside click for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusOpen(false);
      }
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Fetch roles once on mount ──────────────────────────────────────────────
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await roleService.getRoles({ limit: 1000 });
        const rolesData = response.data?.roles || response.data?.data || [];
        setAvailableRoles(rolesData);
      } catch (err) {
        console.error('Failed to load roles:', err);
      }
    };
    fetchRoles();
  }, []); // runs once


  const fetchUsers = async (pageOverride) => {
    const currentPagination = paginationRef.current;
    try {
      setLoading(true);
      const params = {
        page: pageOverride ?? currentPagination.page,
        limit: currentPagination.limit,
        search: debouncedSearch,
        status: filters.status === 'All' ? '' : filters.status,
        role: filters.role === 'All' ? '' : filters.role,
      };

      const response = await userService.getUsers(params);

      const usersData = response.data?.users || response.data?.data || (Array.isArray(response.data) ? response.data : []);

      setUsers(usersData);

      const total = response.data.pagination?.total || response.data.total || usersData.length;
      const limit = response.data.pagination?.limit || response.data.limit || currentPagination.limit;
      const totalPages = response.data.pagination?.totalPages || response.data.totalPages || Math.ceil(total / limit);

      setPagination({
        page: pageOverride ?? (response.data.pagination?.page || response.data.page || currentPagination.page),
        limit,
        total,
        totalPages,
      });
    } catch (err) {
      toastService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };


  // This is the ONLY place a fetch fires when filters change.
  useEffect(() => {
    fetchUsers(1);
  }, [debouncedSearch, filters.status, filters.role]);

  // We skip page 1 here because Effect 1 already handles it on filter change.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchUsers(pagination.page);
  }, [pagination.page]);


  const handleDeleteConfirm = async () => {
    try {
      setSubmitting(true);
      await userService.deleteUser(selectedUser.userId);
      toastService.success('User deleted successfully');
      setShowDeleteModal(false);
      fetchUsers(pagination.page);
    } catch (err) {
      toastService.error('Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };


  const formatDate = (date) => toLocal(date, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  // Only update pagination.page; Effect 2 picks it up.
  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, page }));
  };

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-y-auto p-2">

        {/* Header Section */}
        <div className="border-b border-base-300 flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <h1 className="page-title flex items-center gap-3">
              User Management
            </h1>
            <p className="page-subtitle">Manage your team, create new users and assign roles</p>
          </div>
          {can.users.create() && (
            <button
              onClick={() => navigate('/users/new')}
              className="btn-premium"
            >
              <UserPlus size={18} />
              <span>Create User</span>
            </button>
          )}
        </div>

        {/* Filters and List Area */}
        <div className="pt-6 sm:pt-8 space-y-6">
          {/* Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between glass-panel !bg-base-content/5 p-4 rounded-2xl relative z-[60]">
            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
              <div className="relative flex-1 min-w-[280px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/30" size={18} />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2.5 bg-base-100 border border-base-content/10 rounded-xl text-sm focus:outline-none focus:border-primary/50 transition-all font-bold shadow-sm"
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                />
              </div>

              <div className="relative w-full sm:w-48" ref={roleDropdownRef}>
                <button
                  onClick={() => setIsRoleOpen(!isRoleOpen)}
                  className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-2.5 bg-base-100 border border-base-content/10 text-sm font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none cursor-pointer group shadow-sm transition-all"
                >
                  <span className="truncate flex-1 text-left uppercase text-[10px] tracking-wider">
                    {filters.role === 'All' ? 'All Roles' : (() => {
                      const selectedRole = availableRoles.find(r => r.id === filters.role);
                      return selectedRole?.displayName || selectedRole?.name || filters.role;
                    })()}
                  </span>
                  <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isRoleOpen ? 'rotate-180' : ''}`} />
                </button>

                {isRoleOpen && (
                  <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col max-h-60 overflow-y-auto">
                    <button
                      onClick={() => {
                        setFilters(f => ({ ...f, role: 'All' }));
                        setIsRoleOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 ${filters.role === 'All' ? CLASS_BG_PRIMARY_MUTED : ''}`}
                    >
                      <span className={`text-[11px] font-black uppercase tracking-widest ${filters.role === 'All' ? CLASS_TEXT_PRIMARY : CLASS_TEXT_MUTED_HOVER}`}>
                        All Roles
                      </span>
                      {filters.role === 'All' && <Check size={12} className="text-primary ml-auto" />}
                    </button>
                    {availableRoles
                      .sort((a, b) =>
                        (a.displayName || a.name).localeCompare(
                          (b.displayName || b.name),
                          undefined,
                          { sensitivity: 'base' }
                        )
                      )
                      .map((role) => (
                        <button
                          key={role.key || role.id}
                          onClick={() => {
                            setFilters(f => ({ ...f, role: role.id }));
                            setIsRoleOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${filters.role === role.id ? 'bg-primary/5' : ''}`}
                        >
                          <span
                            className={`text-[11px] font-black uppercase tracking-widest ${filters.role === role.id
                              ? 'text-primary'
                              : 'text-base-content/40 group-hover:text-primary'
                              }`}
                          >
                            {role.displayName || role.name}
                          </span>

                          {filters.role === role.id && (
                            <Check size={12} className="text-primary ml-auto" />
                          )}
                        </button>
                      ))}
                  </div>
                )}
              </div>

              <div className="relative w-full sm:w-48" ref={statusDropdownRef}>
                <button
                  onClick={() => setIsStatusOpen(!isStatusOpen)}
                  className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-2.5 bg-base-100 border border-base-content/10 text-sm font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none cursor-pointer group shadow-sm transition-all"
                >
                  <span className="truncate flex-1 text-left uppercase text-[10px] tracking-wider">
                    {filters.status === 'All' ? 'All Status' : filters.status}
                  </span>
                  <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isStatusOpen ? 'rotate-180' : ''}`} />
                </button>

                {isStatusOpen && (
                  <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col">
                    {['All', 'active', 'inactive'].map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          setFilters(f => ({ ...f, status }));
                          setIsStatusOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${filters.status === status ? CLASS_BG_PRIMARY_MUTED : ''}`}
                      >
                        <span className={`text-[11px] font-black uppercase tracking-widest ${filters.status === status ? CLASS_TEXT_PRIMARY : CLASS_TEXT_MUTED_HOVER}`}>
                          {status === 'All' ? 'All Status' : status}
                        </span>
                        {filters.status === status && <Check size={12} className="text-primary ml-auto" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Main Table */}
          {loading && users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 space-y-4">
              <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-32 glass-panel !bg-base-100/30 rounded-2xl border-dashed border-2 border-base-content/10">
              <div className="w-20 h-20 bg-base-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                <UsersIcon size={40} className="text-base-content/20" />
              </div>
              <h3 className="text-2xl font-black text-base-content mb-2 tracking-tight">No users found</h3>
              <p className="text-base-content/50 max-w-xs mx-auto font-bold uppercase tracking-widest text-[10px]">
                Try adjusting your search or create a new user
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto glass-panel rounded-2xl border border-base-content/5">
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-base-content/5">
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5 px-6">Display Name</th>
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Email</th>
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Security Roles</th>
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Status</th>
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Created Date</th>
                    {(can.users.update() || can.users.delete()) && (
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5 px-6 text-right">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.userId} className="hover:bg-base-content/5 transition-colors border-b border-base-content/5 last:border-0 group">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-black text-base-content tracking-tight">{`${user.displayName}`}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4">
                        <div className=" text-base-content/40 font-bold tracking-tight lowercase">
                          {user.email}
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {user.roles?.length > 0 ? (
                            user.roles.map((role, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[9px] font-black uppercase tracking-widest border border-primary/10 shadow-sm"
                                title={role.description || role.displayName}
                              >
                                {role.displayName || role.key}
                              </span>
                            ))
                          ) : (
                            <span className="text-[10px] font-black text-base-content/20 uppercase tracking-widest italic">No Roles</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="py-4">
                        <span className="text-sm font-mono text-base-content/100">
                          {formatDate(user.createdAt)}
                        </span>
                      </td>
                      {(can.users.update() || can.users.delete()) && (
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {can.users.update() && (
                              <button
                                onClick={() => navigate(`/users/${user.userId}/edit`)}
                                className="p-1.5 text-base-content/30 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                title="Edit User"
                              >
                                <Edit2 size={18} />
                              </button>
                            )}
                            {can.users.delete() && (
                              <button
                                onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }}
                                className="p-2 hover:bg-error/10 hover:text-error rounded-lg transition-all text-base-content/30"
                                title="Delete User"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {pagination.totalPages > 1 && (
                <div className="px-4">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    loading={loading}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete User Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete User"
      >
        <div className="mb-6">
          <p className="text-base-content/60 text-sm leading-relaxed">
            Are you sure you want to delete the account for{' '}
            <strong className="text-base-content">
              {selectedUser?.displayName ||
                `${selectedUser?.firstName} ${selectedUser?.lastName}`}
            </strong>
            ? This action cannot be undone and the user will permanently lose access to the system.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setShowDeleteModal(false)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirm}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {submitting ? 'Deleting...' : 'Delete User'}
          </button>
        </div>
      </Modal>

    </div>
  );
};

export default Users;
